import { useState, useEffect, useRef, useCallback } from 'react';
import axiosInstance from './axiosInstance';

const HEARTBEAT_MS   = 30_000;
const IDLE_MS        = 3 * 60_000;
const OFFLINE_MS     = 10 * 60_000;
const POLL_ONLINE_MS = 20_000;

export const STATUS_COLOR = {
  online:  '#10b981',
  idle:    '#f59e0b',
  offline: '#4b5563',
};

export const STATUS_LABEL = {
  online:  'Çevrimiçi',
  idle:    'Boşta',
  offline: 'Çevrimdışı',
};

export const usePresence = () => {
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [myStatus,    setMyStatus]    = useState('online');
  const lastActivityRef = useRef(Date.now());

  useEffect(() => {
    const onActivity = () => { lastActivityRef.current = Date.now(); };
    const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach(e => window.addEventListener(e, onActivity, { passive: true }));
    return () => events.forEach(e => window.removeEventListener(e, onActivity));
  }, []);

  const getStatus = useCallback(() => {
    const idle = Date.now() - lastActivityRef.current;
    if (idle >= OFFLINE_MS) return 'offline';
    if (idle >= IDLE_MS)    return 'idle';
    return 'online';
  }, []);

  const sendHeartbeat = useCallback(async (status) => {
    try { await axiosInstance.post('/auth/presence/heartbeat', { status }); }
    catch { /* sessizce yoksay */ }
  }, []);

  const fetchOnline = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/auth/presence/online');
      setOnlineUsers(res.data);
    } catch { /* sessizce yoksay */ }
  }, []);

  useEffect(() => {
    const tick = async () => {
      const s = getStatus();
      setMyStatus(s);
      if (s !== 'offline') await sendHeartbeat(s);
    };
    tick();
    const id = setInterval(tick, HEARTBEAT_MS);
    return () => clearInterval(id);
  }, [getStatus, sendHeartbeat]);

  useEffect(() => {
    fetchOnline();
    const id = setInterval(fetchOnline, POLL_ONLINE_MS);
    return () => clearInterval(id);
  }, [fetchOnline]);

  useEffect(() => {
    const onVis = () => {
      if (!document.hidden) {
        lastActivityRef.current = Date.now();
        sendHeartbeat('online');
        fetchOnline();
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [sendHeartbeat, fetchOnline]);

  const goOffline = useCallback(() => sendHeartbeat('offline'), [sendHeartbeat]);

  return { onlineUsers, myStatus, goOffline };
};
